var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useContext, useRef, useMemo, useCallback, useLayoutEffect, useEffect, } from "react";
import { trapEvent } from "../../utils/EventHelpers";
import WithTooltip from "../with-tooltip/WithTooltip";
import MenuContext from "./MenuContext";
import { StyledMenuItem, StyledMenuItemWrapper } from "./MenuItem.style";
import { StyledContents } from "../../utils/StyleUtils";
const MenuItem = (_a) => {
    var { id, "data-insights": insightsAttr, children, expandableContent, variant = "classic", size = "medium", onClick, title, description, disabled, extraText, extraTextMaxWidth } = _a, rest = __rest(_a, ["id", "data-insights", "children", "expandableContent", "variant", "size", "onClick", "title", "description", "disabled", "extraText", "extraTextMaxWidth"]);
    const { selectedItem, focusItem, setSelectedItem, setFocusItem, multiSelect, isContentWidth } = useContext(MenuContext);
    const itemRef = useRef(null);
    const isSelected = useMemo(() => {
        if (multiSelect) {
            const setObj = new Set(selectedItem);
            return setObj.has(id);
        }
        else {
            return selectedItem === id;
        }
    }, [selectedItem, multiSelect, id]);
    useLayoutEffect(() => {
        // set focus if necessary
        if (focusItem === id && itemRef.current) {
            itemRef.current.focus({ preventScroll: true });
        }
    }, [id, focusItem]);
    const handleOnClick = useCallback((e) => {
        if (!disabled) {
            setSelectedItem(id, !!expandableContent, e);
            onClick === null || onClick === void 0 ? void 0 : onClick(e);
        }
    }, [id, setSelectedItem, expandableContent, onClick, disabled]);
    const handleOnKeyDown = useCallback((e) => {
        const keyCode = e.key;
        if (keyCode === "Enter" || keyCode === " ") {
            if (!expandableContent) {
                trapEvent(e);
            }
            setSelectedItem(id, !!expandableContent, e);
            onClick === null || onClick === void 0 ? void 0 : onClick(e);
        }
        if (keyCode === "ArrowRight" && !!expandableContent) {
            trapEvent(e);
            setSelectedItem(id, true, e);
        }
        if (keyCode === "ArrowLeft" && !!expandableContent && selectedItem === id) {
            trapEvent(e);
            setSelectedItem("", true, e);
            // call setFocusItem again (it's already called inside setSelectedItem) to still keep this item focused
            setFocusItem(id);
        }
    }, [expandableContent, onClick, id, selectedItem, setSelectedItem]);
    const handleOnFocus = useCallback(() => {
        // // update the focus item (in menu) when the element get focus from Tab / Shift + Tab pressed
        setFocusItem(id);
    }, [id]);
    const firstRender = useRef(true);
    useEffect(() => {
        firstRender.current = false;
    }, []);
    return useMemo(() => description ? (React.createElement(WithTooltip, { tooltipContents: description, alignment: "right", offset: [0, 8], "aria-hidden": undefined }, (_a) => {
        var { tabIndex, "aria-describedby": ariaDescribedBy, "aria-haspopup": ariaHasPopup } = _a, tooltipTargetProps = __rest(_a, ["tabIndex", "aria-describedby", "aria-haspopup"]);
        return (React.createElement(StyledMenuItemWrapper, Object.assign({}, tooltipTargetProps, rest),
            React.createElement(StyledContents, { as: "span", onFocus: (e) => {
                    if (firstRender.current) {
                        // Prevent tooltip from opening on initial render
                        e.stopPropagation();
                    }
                } },
                React.createElement(StyledMenuItem, { id: id, "data-insights": insightsAttr, isSelected: isSelected, size: size, variant: variant, onClick: handleOnClick, onKeyDown: handleOnKeyDown, onFocus: handleOnFocus, multiSelect: multiSelect, expandableContent: expandableContent, description: description, disabled: disabled, ref: itemRef, title: title, "aria-describedby": ariaDescribedBy, "aria-haspopup": ariaHasPopup, extraText: extraText, extraTextMaxWidth: extraTextMaxWidth, isContentWidth: isContentWidth }, children))));
    })) : (React.createElement(StyledMenuItem, Object.assign({ id: id, "data-insights": insightsAttr, isSelected: isSelected, size: size, variant: variant, onClick: handleOnClick, onKeyDown: handleOnKeyDown, onFocus: handleOnFocus, disabled: disabled, multiSelect: multiSelect, expandableContent: expandableContent }, rest, { ref: itemRef, title: title, extraText: extraText, extraTextMaxWidth: extraTextMaxWidth, isContentWidth: isContentWidth }), children)), [
        id,
        isSelected,
        disabled,
        size,
        variant,
        handleOnClick,
        handleOnFocus,
        handleOnKeyDown,
        multiSelect,
        expandableContent,
        description,
        focusItem,
    ]);
};
export default MenuItem;
//# sourceMappingURL=MenuItem.js.map